import { FormProvider } from "react-hook-form";
import useAddEdit from "./useAddEdit";
import { Dialog, FormInput, FormSelect, Loader, TaskFormContainer } from "@/components";
import { CATEGORY, ID, LAG_DAYS, SUB_CATEGORY, TASK_NAME, TYPE, typeOptions } from "./consts";
import { AddEditProps } from "./types";
import { Button } from "@/components/ui";

const AddEdit = ({open, setOpen, isEdit, onSaveDeps, depsList}: AddEditProps) => {
  const {
    form,
    requiredFields,
    taskList,
    taskData,
    onSubmit,
    isCategoryListLoading,
    isRefetchingSubCategories,
    categoryList,
    subCategoryList,
    isRefetchingTaskList,
    onCancel,
    keys,
    isNew
  } = useAddEdit({setOpen, onSaveDeps, isEdit});
  const {errors} = form.formState;
  const depsTaskIds = new Set(depsList?.map((val: any) => val.dependsOnTaskId?.toString()));

  return (
    <Dialog
      open={open}
      setOpen={setOpen}
      title={isEdit ? `Edit Dependency` : 'Add Dependency'}
      onCancel={onCancel}
      hideFooter
    >
      {isCategoryListLoading || isRefetchingTaskList ? <Loader /> :
        <FormProvider {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)}> 
            <div className="flex flex-col gap-5">
              {isEdit && !isNew && form.watch(ID) ? 
                <TaskFormContainer label="ID" isRequired={requiredFields.includes(ID)}>
                  <FormInput
                    name={ID}
                    control={form.control}
                    label=""
                    placeholder="Enter"
                    error={(errors as any)[ID]?.message}
                    isRequired={requiredFields.includes(ID)}
                    disabled
                  />
                </TaskFormContainer> : <></>}
              <TaskFormContainer label="Category" isRequired={requiredFields.includes(CATEGORY)}>
                <FormSelect
                  key={keys.category}
                  name={CATEGORY}
                  control={form.control}
                  label=""
                  placeholder="Please select"
                  error={errors[CATEGORY]?.message}
                  isRequired={requiredFields.includes(CATEGORY)}
                  options={categoryList?.data?.map((data: any) => ({label: data?.title, value: data?.taskCategoryId?.toString()}))}
                  disabled={isEdit}
                />
              </TaskFormContainer>
              <TaskFormContainer label="Sub Category" isRequired>
                <FormSelect
                  key={keys.subcategory}
                  name={SUB_CATEGORY}
                  control={form.control}
                  label=""
                  placeholder="Please select"
                  error={errors[SUB_CATEGORY]?.message}
                  isRequired={requiredFields.includes(SUB_CATEGORY)}
                  options={subCategoryList?.data?.map((data: any) => ({label: data?.title, value: data?.taskSubCategoryId?.toString()}))}
                  disabled={!form.watch(CATEGORY) || isRefetchingSubCategories || isEdit}
                />
              </TaskFormContainer>
              <TaskFormContainer label="Task Name" isRequired={requiredFields.includes(TASK_NAME)}>
                <FormSelect
                  key={keys.task}
                  name={TASK_NAME}
                  control={form.control}
                  label=""
                  placeholder="Select"
                  error={
                    (errors as any)[TASK_NAME]
                      ?.message
                  }
                  isRequired={requiredFields.includes(
                    TASK_NAME
                  )}
                  options={taskList?.data
                    ?.filter((data: any) => {
                      if (isEdit) return true;
                  
                      const isCurrentTask = data?.taskId === taskData?.taskId;
                      const isAlreadyDependent = depsTaskIds.has(data.taskId?.toString());
                  
                      return !isCurrentTask && !isAlreadyDependent;
                    })
                    ?.map((data: any) => ({
                      label: data?.taskName,
                      value: data?.taskId?.toString(),
                    }))}
                    disabled={isEdit || !form.watch(SUB_CATEGORY) || isRefetchingSubCategories}
                />
              </TaskFormContainer>
              <TaskFormContainer label="Type" isRequired={requiredFields.includes(TYPE)}>
                <FormSelect
                  name={TYPE}
                  control={form.control}
                  label=""
                  placeholder="Enter"
                  error={
                    (errors as any)[TYPE]
                      ?.message
                  }
                  isRequired={requiredFields.includes(
                    TYPE
                  )}
                  options={typeOptions}
                  showSearch={false}
                />
              </TaskFormContainer>
              <TaskFormContainer label="Lag Days" isRequired={requiredFields.includes(LAG_DAYS)}>
                <FormInput
                  name={LAG_DAYS}
                  control={form.control}
                  label=""
                  placeholder="Select"
                  error={
                    (errors as any)[LAG_DAYS]
                      ?.message
                  }
                  isRequired={requiredFields.includes(
                    LAG_DAYS
                  )}
                  endIcon="days"
                />
              </TaskFormContainer>
              <div className="flex justify-end gap-3 mt-5">
                <Button variant="outline" type="button" onClick={() => setOpen(false)}>Cancel</Button>
                <Button type="submit">Save</Button>
              </div>
            </div>
          </form>
        </FormProvider>
      }
    </Dialog>
  )
}

export default AddEdit;